import React, { useEffect, useState } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Routes, Route, Navigate } from 'react-router-dom';
import Reservation from './pages/Reservation';
import Admin from './pages/Admin';
import AdminLogin from './pages/AdminLogin';
import SimpleAuth from './pages/SimpleAuth';
import NotFound from './pages/NotFound';
import theme from './theme'; 
import { TokenMapProvider } from './contexts/TokenMapContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Box, CircularProgress } from '@mui/material';
import { addDoc, collection } from 'firebase/firestore';
import { db } from './firebase';

const PrivateRoute: React.FC<{ children: React.ReactElement; adminOnly?: boolean }> = ({ 
  children, 
  adminOnly = false 
}) => {
  const { user, isAdmin, loading } = useAuth();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/admin-login" replace />;
  }

  if (adminOnly && !isAdmin) {
    return (
      <Box p={4} textAlign="center">
        <h2>Ei käyttöoikeutta</h2>
        <p>Tarvitset admin-oikeudet tämän sivun katseluun.</p>
      </Box>
    );
  }

  return children;
};

const AppRoutes: React.FC = () => {
  const { isAdmin } = useAuth();
  const [isSimpleAuthenticated, setIsSimpleAuthenticated] = useState(false);
  const [simpleAuthLoading, setSimpleAuthLoading] = useState(true);

  useEffect(() => {
    // Tarkista onko käyttäjä jo kirjautunut SimpleAuth:n kautta
    const simpleAuth = localStorage.getItem('simpleAuth');
    if (simpleAuth) {
      handleSimpleLogin(simpleAuth);
    } else {
      setSimpleAuthLoading(false);
    }
  }, []);

  const handleSimpleLogin = async (key: string): Promise<boolean> => {
    try {
      // Hashaa avain SHA-256:lla
      const encoder = new TextEncoder();
      const data = encoder.encode(key);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const hashedKey = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      // Yritä common-avainta (tavallinen käyttäjä)
      await addDoc(collection(db, 'auth_attempts'), {
        type: 'common',
        key: hashedKey,
        timestamp: new Date()
      });
      
      // Common-avain toimi!
      localStorage.setItem('simpleAuth', key);
      setIsSimpleAuthenticated(true);
      setSimpleAuthLoading(false);
      return true;
    } catch (error) {
      console.error('Error during simple auth:', error);
      localStorage.removeItem('simpleAuth');
      setIsSimpleAuthenticated(false);
      setSimpleAuthLoading(false);
      return false;
    }
  };

  if (simpleAuthLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Routes>
      <Route 
        path="/" 
        element={
          isSimpleAuthenticated ? (
            <Reservation isAdmin={isAdmin} />
          ) : (
            <SimpleAuth onLogin={handleSimpleLogin} />
          )
        } 
      />
      <Route path="/admin-login" element={<AdminLogin />} />
      <Route 
        path="/admin" 
        element={
          <PrivateRoute adminOnly>
            <Admin />
          </PrivateRoute>
        } 
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <TokenMapProvider>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <AppRoutes />
        </ThemeProvider>
      </TokenMapProvider>
    </AuthProvider>
  );
};

export default App;
